import { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';

const CONFETTI_COUNT = 90;
const CONFETTI_COLORS = ['#77BD43', '#F49F01', '#FFD700', '#FF6B6B', '#4ECDC4', '#A78BFA'];

/**
 * Full-viewport confetti rain, fired by mounting it. Portals above everything,
 * runs the shared `confettiFall` keyframe (styles/index.css), then calls
 * `onDone` so the parent can unmount. pointer-events-none, never blocks input.
 */
export const ConfettiBurst: React.FC<{ onDone: () => void }> = ({ onDone }) => {
  const particles = useMemo(
    () =>
      Array.from({ length: CONFETTI_COUNT }).map(() => ({
        left: Math.random() * 100,
        delay: Math.random() * 0.5,
        duration: 2.5 + Math.random() * 2,
        color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
        size: 6 + Math.random() * 8,
        rotation: Math.random() * 360,
        drift: (Math.random() - 0.5) * 120,
      })),
    []
  );

  useEffect(() => {
    const t = setTimeout(onDone, 4500);

    return () => clearTimeout(t);
  }, [onDone]);

  return createPortal(
    <div className="fixed inset-0 z-[10001] pointer-events-none">
      {particles.map((p, i) => (
        <span
          key={i}
          className="absolute block rounded-sm"
          style={{
            left: `${p.left}%`,
            top: '-20px',
            width: p.size,
            height: p.size,
            backgroundColor: p.color,
            ['--confetti-rotate' as never]: `${p.rotation}deg`,
            ['--confetti-drift' as never]: `${p.drift}px`,
            animation: `confettiFall ${p.duration}s ease-in ${p.delay}s forwards`,
          }}
        />
      ))}
    </div>,
    document.body
  );
};
